// AI 플랫폼 게이트웨이(8080) API 클라이언트 — SSE 스트리밍 채팅 + 문서 색인 (연동 가이드 v2, ai-orchestration)
import { authorizedFetch, getFreshToken } from './auth';
export interface UploadDocResult {
  status: 'ok' | 'failed';
  title: string;
  docId?: string;
  indexedChunks?: number;
  error?: string;
}

export interface IngestResponse {
  documents: UploadDocResult[];
  totalChunks: number;
}

/** 색인 진행 단계 — 'uploading'만 클라이언트 전송 구간, 나머지는 ingestion-service SSE 이벤트명과 1:1 */
export type IngestStep = 'uploading' | 'received' | 'parsing' | 'parsed' | 'chunking' | 'embedding' | 'indexing' | 'done' | 'error';

export interface IngestProgress {
  step: IngestStep;
  /** 사용자에게 그대로 보여줄 한 줄 문구 */
  label: string;
  /** 0~100. 단계별 고정값 + 임베딩 구간은 청크 비율로 보간 */
  percent: number;
  /** step==='error'일 때만 — 실패 사유 */
  error?: string;
  /** step==='done'일 때만 — 취소(색인 삭제)에 쓸 서버가 만든 문서 id */
  docId?: string;
}

/**
 * UUID v4 생성 — crypto.randomUUID는 보안 컨텍스트(HTTPS·localhost) 전용이라
 * 사설망 IP(예: 192.168.100.56:3000) 접속 시 없음 → getRandomValues로 직접 생성.
 */
function uuidV4() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** 요청 추적용 Transaction-Id — 요청마다 하이픈 제거한 UUID v4 */
function newTransactionId() {
  return uuidV4().replace(/-/g, '');
}

/**
 * 대화 식별자 — 같은 값을 유지하면 서버가 멀티턴 맥락을 기억, 새 대화 = 새 UUID.
 * 서버는 이 값을 로그인 사용자의 UUID(sub)와 합성해 저장하므로(userId:convId),
 * 대화·첨부·이력은 항상 로그인 계정에 종속된다 — 계정이 다르면 같은 ID여도 격리.
 */
export function newConversationId() {
  return uuidV4();
}

/* API 호출 경로 — 게이트웨이는 dev.gennolab.co.jp에만 있으므로,
   dev에서 서빙될 때만 같은 출처 상대경로(nginx → 127.0.0.1:8080)를 쓰고
   그 외 origin(로컬 개발, test.gennolab.co.jp 등)에서는 항상
   https://dev.gennolab.co.jp 절대 URL로 직접 보낸다 (auth.ts와 동일 정책). */
const GW_ORIGIN = window.location.hostname === 'dev.gennolab.co.jp' ? '' : 'https://dev.gennolab.co.jp';
const CHAT_API_BASE = `${GW_ORIGIN}/chat-api`;
const INGEST_API_BASE = `${GW_ORIGIN}/embed-api`;

/**
 * SSE 공용 파서 — 빈 줄(\n\n)을 이벤트 경계로 블록을 나누고
 * event/data 라인을 해석해 콜백으로 전달하는 누적 함수를 만든다.
 * fetch 스트림과 XHR(responseText 증분) 양쪽에서 같은 파서를 쓴다.
 * data는 "data:" 뒤 원문 그대로 (공백 유의 — 토큰 선행 공백 보존을 위해 트리밍하지 않음)
 */
function createSSEParser(onEvent: (event: string, data: string) => void): (chunk: string) => void {
  let buffer = '';
  return (chunk: string) => {
    buffer += chunk;
    let sep;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let event = 'message';
      const dataLines: string[] = [];
      for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5));
      }
      onEvent(event, dataLines.join('\n'));
    }
  };
}

async function readSSE(body: ReadableStream<Uint8Array>, onEvent: (event: string, data: string) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const feed = createSSEParser(onEvent);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    feed(decoder.decode(value, { stream: true }));
  }
}

/** 채팅 요청에 싣는 이미지 — data는 순수 base64 (data URI 헤더 없이) */
export interface ChatImagePayload {
  mimeType?: string;
  data: string;
}

interface StreamChatParams {
  conversationId: string;
  message: string;
  /**
   * 이 턴에 함께 보낼 이미지. 서버 대화 메모리에는 텍스트만 남으므로,
   * 같은 이미지에 대한 후속 질문이면 매 턴 다시 실어야 한다.
   */
  images?: ChatImagePayload[];
  /** 진행 단계 이벤트 (event:step, data:{"step":"질문 분석 중"}) — 단계 문구를 그대로 전달 */
  onStep?: (step: string) => void;
  onChunk?: (chunk: string) => void;
  onDone?: () => void;
}

/** 채팅 API — POST /api/ai/chat/stream 을 SSE로 파싱해 토큰을 콜백으로 전달 */
export async function streamChat({ conversationId, message, images, onStep, onChunk, onDone }: StreamChatParams): Promise<void> {
  const res = await authorizedFetch(`${CHAT_API_BASE}/api/ai/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Transaction-Id': newTransactionId() },
    // images는 있을 때만 — 빈 배열도 허용되지만 텍스트 전용 턴은 기존 요청 그대로 보낸다
    body: JSON.stringify({ conversationId, message, ...(images?.length ? { images } : {}) }),
  });
  if (!res.ok || !res.body) throw new Error('일시적인 문제로 처리하지 못했습니다. 잠시 후 다시 시도하세요.');

  await readSSE(res.body, (event, data) => {
    if (event === 'step') {
      try { onStep?.(JSON.parse(data).step ?? data); } catch { onStep?.(data); }
    } else if (event === 'message') {
      onChunk?.(data); // 토큰 원문 그대로 (JSON 인코딩 아님)
    }
  });
  onDone?.(); // 종료 이벤트 없이 스트림 닫힘 = 완료
}

/* 색인은 SSE가 열리기도 전에 실패할 수 있다(용량 초과·인증 만료 등).
   HTTP 상태코드만 남는 이 구간을 사용자가 읽을 수 있는 문구로 옮긴다. */
function httpFailureReason(status: number): string {
  if (status === 413) return '파일 용량이 허용 한도를 초과했습니다';
  if (status === 401 || status === 403) return '이 작업을 할 권한이 없습니다. 로그인이 만료되었다면 다시 로그인하세요';
  if (status === 415) return '지원하지 않는 파일 형식입니다';
  if (status === 504 || status === 408) return '처리 시간이 초과되었습니다. 파일을 나누어 업로드하세요';
  if (status >= 500) return '일시적인 문제로 문서를 등록하지 못했습니다. 잠시 후 다시 시도하세요';
  return '문서 등록 요청이 받아들여지지 않았습니다';
}

/* 단계별 진행률 기준점 — 파일 전송과 임베딩이 체감 시간의 대부분이라 넓게 배정.
   전송 구간(0~35%)이 비어 있으면 큰 파일일수록 한참 멈춰 있는 것처럼 보인다. */
const UPLOAD_END = 35;
const EMBED_START = 52;
const EMBED_END = 92;

function formatMB(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)}KB` : `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

/**
 * 파일 1건 색인 — POST /api/ingest/file
 * 전송 진행률(XHR upload.onprogress) → SSE 처리 단계(received→parsing→parsed→chunking
 * →embedding→indexing→done/error)를 한 줄기 진행률로 이어 붙인다.
 * fetch는 업로드 진행 이벤트가 없어 XHR을 쓴다 — 응답 SSE는 responseText 증분으로 파싱.
 */
async function uploadSingleDoc(
  file: File,
  conversationId: string,
  onProgress?: (p: IngestProgress) => void,
): Promise<UploadDocResult> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('conversationId', conversationId); // 채팅과 동일한 대화 ID — 대화 범위(visibility:conversation)로 색인

  // 대용량 전송 도중 만료되면 재전송해야 하므로 미리 갱신해둔다
  const token = await getFreshToken();
  // 갱신까지 실패 = 세션 종료(공용 유틸이 이미 재로그인을 통지했다) — 파일을 헛되게 보내지 않는다
  if (!token) throw new Error('세션이 만료됐습니다 — 다시 로그인해 주세요');

  let result: UploadDocResult = { status: 'failed', title: file.name, error: '처리 중 연결이 끊겼습니다' };
  const handleEvent = (event: string, data: string) => {
    let payload: Record<string, unknown> = {};
    try { payload = JSON.parse(data); } catch { /* 이벤트 본문이 JSON이 아니면 빈 객체로 — 단계 문구는 그대로 낸다 */ }

    switch (event) {
      case 'received':
        onProgress?.({ step: 'received', label: '파일 접수 완료', percent: 38 });
        break;
      case 'parsing':
        onProgress?.({ step: 'parsing', label: '문서에서 텍스트 추출 중', percent: 42 });
        break;
      case 'parsed': {
        const chars = Number(payload.chars);
        onProgress?.({
          step: 'parsed',
          label: Number.isFinite(chars) ? `텍스트 추출 완료 (${chars.toLocaleString()}자)` : '텍스트 추출 완료',
          percent: 48,
        });
        break;
      }
      case 'chunking': {
        const chunks = Number(payload.chunks);
        onProgress?.({
          step: 'chunking',
          label: Number.isFinite(chunks) ? `문서를 ${chunks}개 단위로 나누는 중` : '문서를 나누는 중',
          percent: EMBED_START,
        });
        break;
      }
      case 'embedding': {
        // progress는 "8/40" 형태 문자열
        const [doneStr, totalStr] = String(payload.progress ?? '').split('/');
        const done = Number(doneStr);
        const total = Number(totalStr);
        const ratio = Number.isFinite(done) && Number.isFinite(total) && total > 0 ? done / total : 0;
        onProgress?.({
          step: 'embedding',
          label: total > 0 ? `검색용으로 정리 중 ${done}/${total}` : '검색용으로 정리 중',
          percent: Math.round(EMBED_START + (EMBED_END - EMBED_START) * ratio),
        });
        break;
      }
      case 'indexing':
        onProgress?.({ step: 'indexing', label: '검색용으로 저장 중', percent: 96 });
        break;
      case 'done': {
        const chunks = Number(payload.chunks);
        result = {
          status: 'ok',
          title: file.name,
          docId: typeof payload.docId === 'string' ? payload.docId : undefined,
          indexedChunks: Number.isFinite(chunks) ? chunks : undefined,
        };
        onProgress?.({
          step: 'done',
          label: Number.isFinite(chunks) ? `검색 준비 완료 · ${chunks}개 단위` : '검색 준비 완료',
          percent: 100,
          docId: typeof payload.docId === 'string' ? payload.docId : undefined,
        });
        break;
      }
      case 'error': {
        const reason = typeof payload.reason === 'string' ? payload.reason : data;
        result = { status: 'failed', title: file.name, error: reason };
        onProgress?.({ step: 'error', label: reason, percent: 100, error: reason });
        break;
      }
    }
  };

  return new Promise<UploadDocResult>((resolve, reject) => {
    const feed = createSSEParser(handleEvent);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${INGEST_API_BASE}/api/ingest/file`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Transaction-Id', newTransactionId());
    // 서버 SseEmitter가 300초에 닫히므로 그보다 여유 있게
    xhr.timeout = 330_000;

    xhr.upload.onprogress = e => {
      if (!e.lengthComputable) return;
      const ratio = e.loaded / e.total;
      onProgress?.({
        step: 'uploading',
        label: `업로드 중 ${formatMB(e.loaded)} / ${formatMB(e.total)}`,
        percent: Math.round(UPLOAD_END * ratio),
      });
    };
    // 전송은 끝났지만 서버가 첫 이벤트를 내리기 전 — 여기서 멈춰 보이지 않게
    xhr.upload.onload = () => {
      onProgress?.({ step: 'uploading', label: '업로드 완료 · 서버 처리 대기 중', percent: UPLOAD_END });
    };

    // responseText는 누적되므로 아직 파서에 넘기지 않은 뒷부분만 잘라서 먹인다
    let consumed = 0;
    const drain = () => {
      const text = xhr.responseText;
      if (text.length > consumed) {
        feed(text.slice(consumed));
        consumed = text.length;
      }
    };
    xhr.onprogress = drain;

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        // 413 등은 SSE가 아예 열리지 않는다 — 상태코드만으로 사유를 만든다
        reject(new Error(httpFailureReason(xhr.status)));
        return;
      }
      drain(); // 마지막 이벤트가 onprogress 없이 도착했을 수 있다
      resolve(result);
    };
    xhr.onerror = () => reject(new Error('네트워크 오류로 업로드하지 못했습니다'));
    xhr.ontimeout = () => reject(new Error('처리 시간이 초과됐습니다 — 파일을 나눠서 올려보세요'));
    xhr.onabort = () => reject(new Error('업로드가 중단됐습니다'));

    xhr.send(fd);
  });
}

/**
 * 첨부 취소 — 색인에서 문서를 내린다.
 *
 * 화면에서만 지우면 그 문서가 계속 답변 근거로 잡히므로, 칩을 뺄 때 색인도 함께 내려야 한다.
 * docId는 업로드 완료(done) 때 서버가 준 값을 그대로 쓴다 — 프론트가 파일명으로 만들어내면
 * 서버의 정규화(NFC·해시)와 어긋나 엉뚱한 문서를 지울 수 있다.
 */
export async function deleteDoc(docId: string, conversationId: string): Promise<void> {
  const q = new URLSearchParams({ docId, conversationId });
  const res = await authorizedFetch(`${INGEST_API_BASE}/api/ingest/file?${q}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(res.status === 403
      ? '이 대화의 첨부가 아닙니다'
      : '첨부를 취소하지 못했습니다. 잠시 후 다시 시도하세요');
  }
}

interface UploadDocsOptions {
  /** 파일별 진행 상황 — index는 files 배열의 위치 */
  onProgress?: (index: number, p: IngestProgress) => void;
}

/**
 * 동시 업로드 수.
 *
 * 무제한으로 열지 않는 이유가 셋이다.
 *  - 서버가 파일 전체를 메모리에 올린 뒤 처리한다(MultipartFile) — 큰 파일이 겹치면 부담이 크다
 *  - 임베딩은 Azure 호출이라 동시 요청이 많으면 한도에 걸린다
 *  - 회선 대역폭은 어차피 나눠 쓰므로 전송 구간은 늘려도 총 시간이 줄지 않는다
 * 이득은 전송이 아니라 파싱·임베딩(이미지 전사는 10~20초)이 겹치는 데서 나온다.
 */
const UPLOAD_CONCURRENCY = 3;

/** 문서 업로드 API — 파일별로 색인 요청 후 결과를 집계 (기존 응답 형태 유지) */
export async function uploadDocs(
  files: File[],
  conversationId: string,
  { onProgress }: UploadDocsOptions = {},
): Promise<IngestResponse> {
  // 결과는 입력 순서 그대로 채운다 — 완료 순서가 뒤섞여도 호출자가 파일과 짝지을 수 있게
  const documents: UploadDocResult[] = new Array(files.length);
  let cursor = 0;

  const worker = async () => {
    for (;;) {
      const i = cursor++;   // JS는 단일 스레드라 이 증가가 갈라지지 않는다
      if (i >= files.length) {
        return;
      }
      const file = files[i];
      try {
        documents[i] = await uploadSingleDoc(file, conversationId, p => onProgress?.(i, p));
      } catch (e) {
        // 네트워크 단절·HTTP 실패 — SSE가 열리지 않아 error 이벤트도 못 받는 경로
        const reason = e instanceof Error && /[가-힣]/.test(e.message) ? e.message : '일시적인 문제로 처리하지 못했습니다. 잠시 후 다시 시도하세요.';
        documents[i] = { status: 'failed', title: file.name, error: reason };
        onProgress?.(i, { step: 'error', label: reason, percent: 100, error: reason });
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker));

  const totalChunks = documents.reduce((sum, d) => sum + (d.indexedChunks ?? 0), 0);
  return { documents, totalChunks };
}
